import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaExternalLinkAlt,
  FaArrowLeft,
  FaRocket,
  FaCheckCircle,
} from "react-icons/fa";
import {
  PortfolioDataWeb,
  PortfolioDataMobile,
} from "../../../Utils/PortfolioData";
import "./PortfolioDetail.scss";

const PortfolioDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTab = location.state?.tab || "web";

  const allPortfolioData = [...PortfolioDataWeb, ...PortfolioDataMobile];
  const portfolioItem = allPortfolioData.find(
    (item) => item.id === parseInt(id)
  );

  const isWebProject = PortfolioDataWeb.some(
    (item) => item.id === parseInt(id)
  );
  const relatedSource = isWebProject ? PortfolioDataWeb : PortfolioDataMobile;
  const otherProjects = relatedSource.filter(
    (item) => item.id !== parseInt(id)
  );

  const [currentHeroImg, setCurrentHeroImg] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!portfolioItem || portfolioItem.images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentHeroImg((prev) => (prev + 1) % portfolioItem.images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [portfolioItem, id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!portfolioItem) return <div className="loader">Project Not Found</div>;

  return (
    <div className="portfolio-detail-wrapper">
      {/* --- 1. HERO SECTION --- */}
      <section className="hero-banner">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentHeroImg}
            src={portfolioItem.images[currentHeroImg]}
            className="hero-bg"
            initial={hasMounted ? { opacity: 0 } : { opacity: 0.2 }}
            animate={{ opacity: 0.15 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
          />
        </AnimatePresence>

        <div className="hero-content">
          <motion.button
            className="back-btn"
            onClick={() =>
              navigate("/portfolios", {
                state: { tab: selectedTab },
              })
            }
            whileHover={{ x: -5 }}
          >
            <FaArrowLeft /> Back
          </motion.button>

          <motion.div
            className="hero-text"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <span className="category-pill">
              {isWebProject ? "Web Solution" : "Mobile Innovation"}
            </span>
            <h1>{portfolioItem.title}</h1>
          </motion.div>
        </div>
      </section>

      {/* --- 2. MAIN CONTENT --- */}
      <main className="content-container">
        <div className="detail-grid">
          {/* Left Side: Images */}
          <div className="image-stack">
            {portfolioItem.images.map((img, idx) => (
              <motion.div
                className="img-card"
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <img src={img} alt={`Shot ${idx}`} />
              </motion.div>
            ))}
          </div>

          {/* Right Side: Info Sidebar */}
          <aside className="info-sidebar">
            <div className="sticky-card">
              <div className="info-group">
                <h3 className="group-title">Project Overview</h3>
                <p className="main-desc">{portfolioItem.description}</p>
              </div>

              {portfolioItem.keyFeatures && (
                <div className="info-group">
                  <h3 className="group-title">Key Features</h3>
                  <ul className="features-list">
                    {portfolioItem.keyFeatures.map((feature, index) => (
                      <li key={index}>
                        <FaCheckCircle className="check-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="info-group">
                <h3 className="group-title">Technology Stack</h3>
                <div className="tech-pills">
                  {portfolioItem.technologies.map((tech) => (
                    <span key={tech} className="tech-pill">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* --- 2. MAIN CONTENT ke andar CTA group ka updated part --- */}

              {portfolioItem.liveLinks &&
                portfolioItem.liveLinks.length > 0 && (
                  <div className="cta-group info-group">
                    <h3 className="group-title">Live Preview</h3>
                    {portfolioItem.liveLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="primary-cta"
                      >
                        {link.name}{" "}
                        <FaExternalLinkAlt
                          style={{ marginLeft: "8px", fontSize: "0.8rem" }}
                        />
                      </a>
                    ))}
                  </div>
                )}
            </div>
          </aside>
        </div>
      </main>

      {/* --- 3. DISCOVER SLIDER --- */}
      <section className="related-projects">
        <div className="container">
          <div className="section-header">
            <h2>Explore More Masterpieces</h2>
            <div className="line"></div>
          </div>

          <div className="slider-wrapper">
            <div className="infinite-track">
              {[...otherProjects, ...otherProjects].map((project, index) => (
                <div
                  key={`${project.id}-${index}`}
                  className="related-card"
                  onClick={() => navigate(`/portfolio/${project.id}`)}
                >
                  <img src={project.images[0]} alt={project.title} />
                  <div className="card-info">
                    <h4>{project.title}</h4>
                    <span>View Project</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- 4. CTA --- */}
      <section className="bottom-cta">
        <div className="container">
          <motion.div
            className="cta-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="cta-content">
              <span className="cta-tag">Ready to start?</span>
              <h2>Have an ambitious idea?</h2>
              <p>
                Let's turn your vision into a digital reality with our expert
                team.
              </p>

              <Link to="/contact-us" className="minimal-btn">
                Let's Talk Business <FaRocket />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PortfolioDetail;
